// Base URL for the CDN image transformation service (Cloudflare Images or compatible)
// Append transformation options and a source URL to get a resized/cropped image.
// Format: {CDN_THUMBNAIL_BASE_URL}/{options}/{source-image-url}
const CDN_THUMBNAIL_BASE_URL = process.env.NX_PUBLIC_CDN_BASE_URL || process.env.NEXT_CDN_BASE_URL;

const isAbsoluteUrl = (url: string) => /^https?:\/\//i.test(url);

// Remove trailing slashes so URLs can be safely concatenated with a single "/"
const normalizeBaseUrl = (url: string) => url.replace(/\/+$/, "");

// Remove leading slashes so path segments can be safely joined
const normalizePath = (url: string) => url.replace(/^\/+/, "");

const hasFileExtension = (url: string) => /\.[a-z0-9]+([?#].*)?$/i.test(url);

// Builds the GCS bucket root URL from env variables.
// NX_PUBLIC_GCP_URL  — base GCP storage host
// NX_PUBLIC_BUCKET_NAME — bucket name appended after the host
const getGcbBaseUrl = () => {
  const gcpBaseUrl = normalizeBaseUrl(
    process.env.NX_PUBLIC_GCP_URL || process.env.NEXT_GCP_URL || "",
  );
  const bucketName = normalizePath(
    process.env.NX_PUBLIC_BUCKET_NAME || process.env.NEXT_GCP_BUCKET_NAME || "",
  );

  return bucketName ? `${gcpBaseUrl}/${bucketName}` : gcpBaseUrl;
};

/**
 * Converts a relative image path into a full GCS (Google Cloud Storage) URL.
 * If the URL is already absolute it is returned as-is.
 * An optional file extension is appended when the path has none.
 */
export const formGCBURL = (url = "", extension = "") => {
  if (!url) {
    return "";
  }

  if (isAbsoluteUrl(url)) {
    return url;
  }

  const normalizedExtension = extension.replace(/^\./, "");
  const normalizedPath = normalizePath(url);
  const imagePath =
    normalizedExtension && !hasFileExtension(normalizedPath)
      ? `${normalizedPath}.${normalizedExtension}`
      : normalizedPath;
  const gcbBaseUrl = getGcbBaseUrl();
  const resolvedUrl = gcbBaseUrl ? `${gcbBaseUrl}/${imagePath}` : imagePath;

  return resolvedUrl;
};

/**
 * Wraps a GCS source URL with CDN transformation options to produce a thumbnail URL.
 * Example result: https://img.cdn-asset.store/cdn-cgi/image/width=320,quality=85,format=webp/https://storage.googleapis.com/.../image.jpg
 */
export const formThumbnailFromGCBURL = (
  url = "",
  extension = "",
  options = "width=320,quality=85,format=webp",
) => {
  const sourceUrl = formGCBURL(url, extension);

  if (!sourceUrl) {
    return "";
  }

  const normalizedOptions = options.replaceAll(/^\/+|\/+$/g, "");
  const thumbnailUrl = normalizedOptions
    ? `${CDN_THUMBNAIL_BASE_URL}/${normalizedOptions}/${sourceUrl}`
    : `${CDN_THUMBNAIL_BASE_URL}/${sourceUrl}`;

  return thumbnailUrl;
};

/**
 * Converts a 4-point crop vertex array (TL, TR, BL, BR) into a bounding box.
 * Vertex format: [{ x, y }, { x, y }, { x, y }, { x, y }]
 * Returns null when the vertices produce a zero-size box or are invalid.
 */
export const getCropBoundsFromVertices = (vertices: any[] = []) => {
  if (!Array.isArray(vertices) || vertices.length !== 4) {
    return null;
  }

  const xCoordinates = vertices.map((vertex) => Number(vertex?.x ?? 0));
  const yCoordinates = vertices.map((vertex) => Number(vertex?.y ?? 0));
  const left = Math.max(0, Math.min(...xCoordinates));
  const top = Math.max(0, Math.min(...yCoordinates));
  const right = Math.max(...xCoordinates);
  const bottom = Math.max(...yCoordinates);
  const width = Math.max(0, right - left);
  const height = Math.max(0, bottom - top);
  if (!width || !height) {
    return null;
  }

  return {
    left,
    top,
    width,
    height,
  };
};

/**
 * Builds a CDN URL that delivers a manually-cropped region of a GCS image.
 * The crop box is expressed as CDN `trim.*` parameters derived from the saved crop vertices.
 * If no valid crop box is found it falls back to a plain thumbnail URL.
 *
 * Example trim options: trim.left=100,trim.top=50,trim.width=800,trim.height=600
 */
export const formManualCropPreviewFromGCBURL = (
  url = "",
  vertices: any[] = [],
  extension = "",
  options = "",
) => {
  const bounds = getCropBoundsFromVertices(vertices);

  if (!bounds) {
    return formThumbnailFromGCBURL(url, extension, options);
  }

  const cropOptions = [
    `trim.left=${bounds.left}`,
    `trim.top=${bounds.top}`,
    `trim.width=${bounds.width}`,
    `trim.height=${bounds.height}`,
    options,
  ].join(",");

  return formThumbnailFromGCBURL(url, extension, cropOptions);
};

/**
 * Central helper — returns a CSS `background-image` value for a content item's background.
 *
 * Resolution order (first non-empty result wins):
 *  1. `preview_url`  — a pre-generated crop preview URL already stored in published_images
 *  2. GCB path + crop vertices — builds a CDN trim URL from the original image's GCB path
 *  3. `folder_path` + `fallbackBaseUrl` — legacy GCP path with AVIF/ext URLs
 *  4. `fallbackUrl` — raw absolute URL (e.g. dspace, external)
 *
 * @param publishedImages  Array of per-ratio crop hints saved by the authoring tool
 * @param originalImage    Original image metadata including original_image_relative_path and ext
 * @param defaultRatio     Aspect ratio name to look up in publishedImages (e.g. "landscape")
 * @param options          CDN transformation options string (width, quality, format)
 * @param fallbackUrl      Raw URL used as last resort when nothing else resolves
 * @param fallbackBaseUrl  GCP base URL used for legacy folder_path fallback
 */
export const formPublishedImageBackgroundValue = ({
  publishedImages = [],
  originalImage = {},
  defaultRatio = "landscape",
  options = "width=1440,quality=85,format=webp",
  fallbackUrl = "",
  fallbackBaseUrl = "",
}: {
  publishedImages?: any[];
  originalImage?: any;
  defaultRatio?: string;
  options?: string;
  fallbackUrl?: string;
  fallbackBaseUrl?: string;
}) => {
  const selectedImage =
    publishedImages?.find(({ aspect_ratio }: any) => aspect_ratio === defaultRatio) ||
    publishedImages?.[0];
  // const previewUrl = selectedImage?.preview_url || "";
  const cropVertex = selectedImage?.crop_vertex || [];
  const folderPath = selectedImage?.folder_path || "";
  const gcbRelativePath = originalImage?.original_image_relative_path || "";
  const gcbExt = originalImage?.ext || "";

  // if (previewUrl) {
  //   return `url('${previewUrl}')`;
  // }

  if (gcbRelativePath) {
    const cropUrl = formManualCropPreviewFromGCBURL(gcbRelativePath, cropVertex, gcbExt, options);

    if (cropUrl) {
      return `url('${cropUrl}')`;
    }
  }

  if (folderPath && fallbackBaseUrl) {
    const normalizedBaseUrl = normalizeBaseUrl(fallbackBaseUrl);
    const normalizedFolderPath = normalizePath(folderPath);
    const normalizedExt = gcbExt.replace(/^\./, "");
    const avifUrl = `${normalizedBaseUrl}/${normalizedFolderPath}.avif`;

    return normalizedExt
      ? `url('${avifUrl}'), url('${normalizedBaseUrl}/${normalizedFolderPath}.${normalizedExt}')`
      : `url('${avifUrl}')`;
  }

  if (fallbackUrl && (fallbackUrl.search("dspace") !== -1 || isAbsoluteUrl(fallbackUrl))) {
    return fallbackUrl;
  }

  return "";
};

/**
 * Determines the crop ratio name and image width for the currently active viewport breakpoint.
 * This is a pure utility — the caller is responsible for computing the media-query flags
 * (e.g. via MUI `useMediaQuery`) and passing them in.
 *
 * Breakpoints are evaluated smallest → largest; the first active flag wins.
 * Falls back to 1440px (xl) when none match.
 *
 * @param mediaFlags        Boolean flags for each MUI breakpoint (true when viewport is within that range)
 * @param imgOrder          Per-breakpoint ratio overrides from the content item
 *                          (key = breakpoint width as string, e.g. "768"; value = ratio name e.g. "square")
 * @param defaultBreakpoints Default ratio name for each breakpoint (e.g. from ConstantData.breakpoints)
 *
 * @returns { ratio: string, width: number } — ratio name and pixel width for the active breakpoint
 */
export const getResponsiveCropConfig = (
  mediaFlags: {
    less_than_320: boolean;
    less_than_600: boolean;
    less_than_768: boolean;
    less_than_1024: boolean;
    less_than_1280: boolean;
    less_than_1440: boolean;
  },
  imgOrder: Record<string, string> = {},
  defaultBreakpoints: Record<string, string> = {},
) => {
  // Ordered smallest → largest; use the first breakpoint that is active
  const breakpointChecks = [
    { active: mediaFlags.less_than_320, size: 320 },
    { active: mediaFlags.less_than_600, size: 600 },
    { active: mediaFlags.less_than_768, size: 768 },
    { active: mediaFlags.less_than_1024, size: 1024 },
    { active: mediaFlags.less_than_1280, size: 1280 },
    { active: mediaFlags.less_than_1440, size: 1440 },
  ];

  const matched = breakpointChecks.find(({ active }) => active) ?? { size: 1440 };
  const key = String(matched.size);

  return { ratio: imgOrder[key] || defaultBreakpoints[key], width: matched.size };
};
